<template>
  <notes-filter
    v-test-id="'notes-filter'"
    :form-items="formItems()"
    :custom-filter-form="customFilterForm"
    @filters-updated="updateFilters"
  >
    <el-popover
      v-model="showFilters"
      slot-scope="{filter, clear}"
      placement="bottom"
      width="440"
      trigger="click"
      data-element="notes-filter-popover-button"
    >
      <el-form
        ref="filterFormRef"
        :model="formFilters"
        labelPosition="top"
      >
        <el-form-item
          prop="date_added"
          label="Created on"
        >
          <el-date-picker
            v-model="formFilters.date_added"
            :picker-options="datePickerOptions"
            type="daterange"
            align="right"
            range-separator="-"
            start-placeholder="Start date"
            end-placeholder="End date"
            data-element="filter-data-range"
          />
        </el-form-item>
        <el-form-item
          prop="creator"
          label="Author"
        >
          <el-select
            v-model="formFilters.creator"
            filterable
            default-first-option
            placeholder="Choose author"
            data-element="filter-author"
          >
            <el-option
              v-for="item in authors"
              :key="item.username"
              :label="item.displayName"
              :value="item.username"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          prop="note_type"
          label="Note type"
        >
          <el-select
            v-model="formFilters.note_type"
            filterable
            default-first-option
            placeholder="Choose note type"
            data-element="filter-note-type"
          >
            <el-option
              v-for="item in noteTypes"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          prop="tags"
          label="Tags"
        >
          <el-select
            v-model="formFilters.tags"
            multiple
            :multiple-limit=10
            filterable
            default-first-option
            placeholder="Choose tags"
            data-element="filter-note-tag"
          >
            <el-option
              v-for="item in noteTags"
              :key="item.id"
              :label="item.name"
              :value="item.name"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <slot
          name="filter-form"
          :form="formFilters"
        />
      </el-form>

      <div class="l-flex-grid l-flex-grid--h-space-between">
        <el-button
          class="u-grow"
          data-test="popover-clear-filters"
          @click="clearFilters(clear)">
          Clear
        </el-button>
        <el-button
          class="u-grow"
          data-test="popover-apply-filters"
          type="primary"
          @click="applyFilters(filter)">
          Filter
        </el-button>
      </div>
      <el-badge
        :hidden="hidePopoverBadge"
        is-dot
        slot="reference"
      >
        <el-button
          class="u-p2"
        >
          <fa-icon
            size="lg"
            icon="filter"
          />
        </el-button>
      </el-badge>
    </el-popover>
  </notes-filter>
</template>

<script>
import NotesFilter from '#/notes/components/NotesFilter'
import noteAuthorsActions from '#/notes/store/authors/actionTypes'
import {cloneDeep, mapValues, isEmpty} from 'lodash'
import {mapState, mapActions} from 'vuex'
import {getDateRange} from '#/notes/components/DateRangeSolver'

export default {
  name: 'NotesFilterPopover',
  components: {
    NotesFilter
  },
  props: {
    customFilterForm: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      showFilters: false,
      formFilters: this.initializeFormFilters(),
      datePickerOptions: {
        shortcuts: [
          {
            text: 'Last 7 days',
            onClick: this.getPastDaysHandler(1, 'weeks', 'day', true)
          }, {
            text: 'Last 30 days',
            onClick: this.getPastDaysHandler(1, 'months', 'day', true)
          }, {
            text: 'Last 90 days',
            onClick: this.getPastDaysHandler(3, 'months', 'day', true)
          }, {
            text: 'Last week',
            onClick: this.getPastDaysHandler(1, 'weeks', 'week', false)
          }, {
            text: 'Last month',
            onClick: this.getPastDaysHandler(1, 'months', 'month', false)
          }, {
            text: 'Last 3 months',
            onClick: this.getPastDaysHandler(3, 'months', 'month', false)
          }
        ]
      }
    }
  },
  watch: {
    customFilterForm: {
      handler (form) {
        this.initializeFilterForm()
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('notes', [
      'userFilters',
      'noteTypes',
      'noteTags'
    ]),
    ...mapState('notes/authors', [
      'authors'
    ]),
    hidePopoverBadge () {
      return isEmpty(this.userFilters)
    }
  },
  methods: {
    initializeFormFilters () {
      return {
        date_added: null,
        tags: [],
        note_type: null
      }
    },
    ...mapActions({
      fetchAuthors: noteAuthorsActions.FETCH_AUTHORS
    }),
    applyFilters (filter) {
      this.showFilters = false
      filter(this.formFilters)
    },
    clearFilters (clear) {
      this.initializeFilterForm()
      clear()
    },
    initializeFilterForm () {
      const newForm = {
        ...this.formFilters,
        ...this.customFilterForm
      }
      this.formFilters = mapValues(newForm, i => isEmpty(i) ? null : i)
    },
    updateFilters (filters) {
      this.formFilters = mapValues(this.formFilters, (_, key) => (filters[key] || this.initializeFormFilters()[key]))
      this.$emit('filters-updated')
    },
    formItems () {
      const formRef = this.$refs.filterFormRef
      return formRef ? formRef.$children : []
    },
    getPastDaysHandler (offset, unitOfTime, startOf, endDateIsToday) {
      return (picker) => {
        picker.$emit('pick', getDateRange(offset, unitOfTime, startOf, endDateIsToday))
      }
    }
  },
  mounted () {
    // As the state of filter form values are stored in `userFilters`, we can restore the filter form values
    // by merging `userFilters` into `formFilters`.
    this.formFilters = {...this.formFilters, ...cloneDeep(this.userFilters)}
    this.fetchAuthors()
  }
}
</script>
